<template>
  <div class="attendance-container">
    <div class="header">
      <h2>{{ session?.course?.name }} Attendance</h2>
      <h3>{{ formatDate(session?.start_at) }}</h3>
      <div class="controls">
        <button @click="addAllStudents" :disabled="missingStudents.length === 0" class="add-all-button">+ Add All Students</button>
        <button @click="saveAttendance" :disabled="!hasChanges">Save</button>
        <button @click="goBack" class="back-button">← Back to Sessions</button>
      </div>
    </div>

    <div class="tables-container">
      <!-- Enrolled Students Table -->
      <div class="table-wrapper">
        <h3>Enrolled Students</h3>
        <table class="student-table">
          <thead>
            <tr>
              <th>Student</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="student in enrolledStudents" 
              :key="student.student_id"
              draggable="true"
              @dragstart="startDrag($event, student, 'enrolled')"
              @dragend="isDragging = false"
            >
              <td>{{ student.name }}</td>
              <td>{{ getEnrollmentStatus(student.student_id) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Attendance Records Table -->
      <div class="table-wrapper">
        <h3>Attendance Records</h3>
        <table 
          class="attendance-table"
          @dragover.prevent
          @dragenter.prevent
          @drop="handleDrop($event, 'attendance')"
        >
          <thead>
            <tr>
              <th>Student</th>
              <th>Status</th>
              <th>Note</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="(attendance, index) in attendances" 
              :key="attendance.student_id"
              draggable="true"
              @dragstart="startDrag($event, attendance, 'attendance')"
              @dragend="isDragging = false"
            >
              <td>{{ attendance.student.name }}</td>
              <td>
                <select v-model="attendance.status">
                  <option v-for="(label, value) in statusOptions" 
                          :value="value"
                          :selected="attendance.status === value">
                    {{ label }}
                  </option>
                </select>
              </td>
              <td>
                <input type="text" v-model="attendance.note" placeholder="Note...">
              </td>
              <td>
                <button @click="onDeleteAttendance(index)">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery, useMutation } from '@vue/apollo-composable';
import { 
  BULK_CREATE_ATTENDANCE,
  BULK_UPDATE_ATTENDANCE,
  GET_SESSION,
  CREATE_ATTENDANCE,
  DELETE_ATTENDANCE
} from '../graphql/queries';

const route = useRoute();
const router = useRouter();
const sessionId = parseInt(route.params.session_id);

const statusOptions = {
  PRESENT: 'Present',
  LATE: 'Late',
  LEFT_EARLY: 'Left Early',
  PERSONAL_LEAVE: 'Personal Leave',
  SICK_LEAVE: 'Sick Leave',
  ABSENT: 'Absent'
};

const isDragging = ref(false);
const dragItem = ref(null);
const dragSource = ref('');

const emit = defineEmits(['save']);

// Get session details with enrolled students and attendance
const { result: sessionResult, refetch: refetchSession } = useQuery(
  GET_SESSION,
  () => ({ session_id: sessionId }),
  { fetchPolicy: 'network-only' }
);

// Computed properties
// Session details
const session = computed(() => sessionResult.value?.session || {});

// Enrolled (& Active) students (from StudentCourse)
const enrolledStudents = computed(() => {
  return session.value.course?.students?.filter(sc => !sc.status || sc.status !== 'Inactive').map(sc => sc.student) || [];
});

// Missing students (students who are enrolled but not in attendance records)
const missingStudents = computed(() => {
  return enrolledStudents.value.filter(student => {
    return !attendances.value.some(att => att.student_id === student.student_id);
  });
});

// Current attendance records
const originalAttendances = ref([]);
const attendances = ref([]);

watch(sessionResult, (newVal) => {
  if (newVal?.session) {
    originalAttendances.value = JSON.parse(JSON.stringify(newVal.session.attendances || []));
    attendances.value = JSON.parse(JSON.stringify(newVal.session.attendances || []));
  }
});

// Drag and drop functions
const startDrag = (event, item, source) => {
  isDragging.value = true;
  dragItem.value = item;
  dragSource.value = source;
  event.dataTransfer.effectAllowed = 'move';
};

const handleDrop = async (event, target) => {
  if (!isDragging.value || dragSource.value === target) return;
  
  if (target === 'attendance') {
    // Add to attendance
    const student = dragItem.value;
    if (!attendances.value.some(a => a.student_id === student.student_id)) {
      const newAttendance = {
        student_id: student.student_id,
        session_id: sessionId,
        status: 'PRESENT',
        note: null,
        student: student
      };
      attendances.value.push(newAttendance);
      
      // Call API to add attendance
      await addAttendanceRecord(student.student_id);
    }
  }
  
  isDragging.value = false;
  dragItem.value = null;
};

const onDeleteAttendance = async (index) => {
  const studentId = attendances.value[index].student_id;
  attendances.value.splice(index, 1);
  
  // Call API to delete attendance
  await deleteAttendanceRecord(studentId);
};

// Get enrollment status for enrolled students
const getEnrollmentStatus = (studentId) => {
  const enrollment = session.value.course?.students?.find(
    sc => sc.student_id === studentId
  );
  return enrollment?.status || 'Active';
};

// Mutation for adding attendance
const { mutate: createAttendance } = useMutation(CREATE_ATTENDANCE);
const addAttendanceRecord = async (studentId) => {
  await createAttendance({
    student_id: studentId,
    session_id: sessionId,
    status: 'PRESENT'
  });
  refetchSession();
};

// Mutation for removing attendance
const { mutate: deleteAttendance } = useMutation(DELETE_ATTENDANCE);
const deleteAttendanceRecord = async (studentId) => {
  await deleteAttendance({
    student_id: studentId,
    session_id: sessionId
  });
  refetchSession();
};

// Mutation for bulk create
const { mutate: bulkCreate } = useMutation(BULK_CREATE_ATTENDANCE);
const addAllStudents = async () => {
  try{
    const inputs = missingStudents.value.map(student => ({
      student_id: student.student_id,
      session_id: sessionId,
      status: 'PRESENT',
      note: null
    }));
    await bulkCreate({ inputs });
    
    // Show success message
    alert(`Added ${missingStudents.value.length} students to attendance.`);
  } catch (error) {
    console.error('Error adding students to attendance:', error);
    alert('Failed to add all students.');
  } finally {
    refetchSession();
  }
};

// Mutation for bulk update
const { mutate: bulkUpdate } = useMutation(BULK_UPDATE_ATTENDANCE);
const saveAttendance = async () => {
  const changes = attendances.value.map(att => ({
    student_id: att.student_id,
    session_id: sessionId,
    status: att.status,
    note: att.note || null
  }));

  await bulkUpdate({ inputs: changes });
  originalAttendances.value = JSON.parse(JSON.stringify(attendances.value));
  emit('save');
  refetchSession();
};

const hasChanges = computed(() => {
  return JSON.stringify(attendances.value) !== JSON.stringify(originalAttendances.value);
});

const goBack = () => {
  router.go(-1);
};

const formatDate = (dateString) => new Date(dateString).toLocaleDateString();
</script>

<style scoped>
.attendance-container {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.tables-container {
  display: flex;
  gap: 2rem;
}

.table-wrapper {
  flex: 1;
  min-width: 0;
}

h3 {
  margin-bottom: 1rem;
}

.student-table,
.attendance-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.5rem;
}

.student-table th,
.student-table td,
.attendance-table th,
.attendance-table td {
  padding: 0.75rem;
  border: 1px solid #ddd;
  text-align: left;
}

.student-table th,
.attendance-table th {
  background-color: #f5f5f5;
  position: sticky;
  top: 0;
}

.student-table tr:hover,
.attendance-table tr:hover {
  background-color: #f9f9f9;
}

.student-table tr[draggable="true"],
.attendance-table tr[draggable="true"] {
  cursor: move;
}

.student-table tr[draggable="true"]:hover,
.attendance-table tr[draggable="true"]:hover {
  background-color: #e6f7ff;
}

select, input {
  padding: 0.375rem;
  width: 100%;
}

button {
  padding: 0.5rem 1rem;
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
}

button:hover {
  background-color: #e0e0e0;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.add-all-button {
  background-color: #4CAF50;
  color: white;
  margin-right: 1rem;
}

.add-all-button:hover {
  background-color: #45a049;
}

.back-button {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
}

.back-button:hover {
  color: #333;
}
</style>